import _get from 'lodash/get';
import _compact from 'lodash/compact';
import _isEmpty from 'lodash/isEmpty';
import _clone from 'lodash/clone';
import I18n from 'i18n-js';
import { combineEpics, ofType } from 'redux-observable';
import { concat, EMPTY, merge, of, timer, forkJoin } from 'rxjs';
import {
  mergeMap,
  pairwise,
  switchMap,
  withLatestFrom,
  catchError,
} from 'rxjs/operators';
import { FIELD_NAME } from 'views/v2/home/constants';
import {
  getFailedConcurrentBookingNotification,
  getFailedNotification,
  getSuccessNotification,
} from 'views/v2/home/order-notification';
import fetchUtil from 'common/utils/fetch';
import {
  getQuoteToRender,
  toBookAgain,
  toGetQuote,
  toRouteOptimisationRequest,
  validRecipientLocations,
} from 'helper/order';
import { getSenderStorage } from 'helper/storage';
import { isPaxUser } from 'helper/grab-id-auth-helper';
import { getCompanyID } from 'helper/user';
import { getUserCountryCodeFromState } from 'selectors/app-selector';

import { buildHeaders } from 'actions/fetch-helper';
import endpoints from 'actions/booking-endpoints';
import {
  globalInitFailureAction,
  pushNotificationAction,
  LOCATION_DRAWER_ROUTE_OPTIMIZATION,
} from 'actions/app-actions';
import {
  BOOK_AGAIN,
  CLEAR_ORDERS_ACTION_V2,
  CREATE_ORDER,
  CREATE_ORDER_V2,
  FETCH_ALL_PAYMENT_METHODS,
  FETCH_QUOTE_V2,
  FETCH_SERVICE_TYPES_V2,
  fetchQuoteActionV2,
  fetchServiceTypesActionV2,
  getAllPaymentMethodsSuccessV2,
  getAllPaxPaymentMethodsV2,
  UPDATE_BULK_ORDER,
  UPDATE_ORDER,
  updateBulkOrderAction,
  updateBulkOrderShallowAction,
  updateOrderAction,
  updateOrderShallowAction,
  PAX_FETCH_ALL_PAYMENT_METHODS,
  getAllPaxPaymentMethodsSuccessV2,
  PAX_FETCH_SERVICE_TYPES,
  paxFetchServiceTypesAction,
} from 'actions/booking-actions';
import { getQuoteErrNotiObservable } from 'helper/quote';
import { fetchPaxPaymentMethods, getOptimizedRoute } from './epic-helpers';
import {
  getAlternativeFare,
  getOptimizedSteps,
} from '../../helper/quoteV2-helper';
import {
  QUOTE_OPTIMIZED,
  QUOTE_OPTIMIZED_ALLOW_REVERT,
} from '../../constants/booking-constants';
import { NOTIFICATION_TYPE } from '../../components/v2/notification/notification';
import { buildCredentials } from '../../helper/session-helper';

const bookAgainEpic = (action$, state$) =>
  action$.pipe(
    ofType(BOOK_AGAIN),
    mergeMap(async action => {
      const { order, pois } = action.payload;
      const orderToBookAgain = toBookAgain(order, pois);
      // dont use updateOrderShallowAction here because need to check and fetch corresponding service types
      return updateOrderAction(orderToBookAgain);
    }),
  );

const updateOrderEpic = (action$, state$) => {
  const statePairs$ = state$.pipe(pairwise());
  return action$.pipe(
    ofType(UPDATE_ORDER),
    withLatestFrom(statePairs$),
    switchMap(([action, [oldState]]) => {
      const state = state$.value;
      const order = _get(state$, 'value.booking.order', {});
      const senderCityID = _get(order, 'senderLocation.address.cityId');
      const prevSenderCityID = _get(
        oldState,
        'booking.order.senderLocation.address.cityId',
      );

      if (senderCityID !== prevSenderCityID) {
        // Get existed serviceTypes
        const serviceTypes = _get(
          state,
          `booking.serviceTypeMap.${senderCityID}`,
        );
        if (serviceTypes) {
          const newOrder = {
            ...order,
            serviceTypes,
          };
          return of(updateOrderAction(newOrder));
        }

        // Fetch new serviceTypes
        return isPaxUser()
          ? of(paxFetchServiceTypesAction(senderCityID))
          : of(
              fetchServiceTypesActionV2(
                senderCityID,
                getCompanyID(state.app.userInfo),
              ),
            );
      }

      return EMPTY;
    }),
  );
};

const updateBulkOrderEpic = (action$, state$) => {
  const statePairs$ = state$.pipe(pairwise());
  return action$.pipe(
    ofType(UPDATE_BULK_ORDER),
    withLatestFrom(statePairs$),
    switchMap(([action, [oldState]]) => {
      const state = state$.value;
      const newOrders = _get(state$, 'value.booking.orders', []);
      const oldOrders = _get(oldState, 'booking.orders', []);
      const observables = [];
      let requireServiceTypeUpdate = false;

      newOrders.forEach((order, index) => {
        const senderCityID = _get(order, 'senderLocation.address.cityId');
        const oldOrder = _get(oldOrders, `[${index}]`);
        const prevSenderCityID = oldOrder
          ? _get(oldOrder, 'senderLocation.address.cityId')
          : null;

        if (!senderCityID) return;

        if (senderCityID !== prevSenderCityID) {
          requireServiceTypeUpdate = true;
          // Get existed serviceTypes
          const serviceTypes = _get(
            state,
            `booking.serviceTypeMap.${senderCityID}`,
          );
          if (serviceTypes) {
            newOrders.splice(index, 1, {
              ...order,
              serviceTypes,
            });
          } else {
            // Fetch new serviceTypes
            observables.push(
              isPaxUser()
                ? of(paxFetchServiceTypesAction(senderCityID))
                : of(
                    fetchServiceTypesActionV2(
                      senderCityID,
                      getCompanyID(state.app.userInfo),
                    ),
                  ),
            );
          }
        }
      });
      if (!requireServiceTypeUpdate) return EMPTY;

      // fetch new service types if any is required
      if (!_isEmpty(observables)) return concat(...observables);

      // No need to fetch new service types, update orders directly
      return of(
        updateBulkOrderAction({
          orders: newOrders,
        }),
      );
    }),
  );
};

const fetchServiceTypeEpic = (action$, state$) =>
  action$.pipe(
    ofType(FETCH_SERVICE_TYPES_V2.SUCCESS, PAX_FETCH_SERVICE_TYPES.SUCCESS),
    switchMap(action => {
      const state = state$.value;
      const userSafeID = _get(state, 'app.userInfo.user.safeID');
      const cityID = isPaxUser()
        ? _get(action, 'body.input.queryParams.cityID').toString()
        : _get(action, 'body.request.queryParams.cityID', '').toString();

      const serviceTypes = _get(state, `booking.serviceTypeMap.${cityID}`);
      let order = _get(state, 'booking.order', {}); // v2 - single order
      if (_isEmpty(order)) {
        order = getSenderStorage(cityID, userSafeID);
      }

      const orders = _get(state, 'booking.orders', []); // v2 - bulk order
      const cityIDInOrder = _get(
        order,
        'senderLocation.address.cityId',
        '',
      ).toString();
      const serviceTypeField = {
        name: FIELD_NAME.SERVICE_TYPE,
        value: _get(serviceTypes, '[0].ID', '').toString(),
      };

      let newOrder = {};
      let newOrders = [];
      let shouldUpdateSingleOrder = false;
      let shouldUpdateBulkOrder = false;

      if (cityID === cityIDInOrder || !order.serviceTypes) {
        newOrder = {
          ...order,
          serviceTypes,
          [FIELD_NAME.SERVICE_TYPE]: serviceTypeField,
        };
        shouldUpdateSingleOrder = true;
      }

      if (_isEmpty(orders)) {
        shouldUpdateBulkOrder = true;
        newOrders = [
          {
            serviceTypes,
            [FIELD_NAME.SERVICE_TYPE]: serviceTypeField,
          },
        ];
      } else {
        newOrders = orders.reduce((acc, o) => {
          if (
            cityID ===
              _get(o, 'senderLocation.address.cityId', '').toString() ||
            !o.serviceTypes
          ) {
            shouldUpdateBulkOrder = true;
            return acc.concat({
              ...o,
              serviceTypes,
              [FIELD_NAME.SERVICE_TYPE]: serviceTypeField,
            });
          }
          return acc.concat(o);
        }, []);
      }

      return concat(
        shouldUpdateSingleOrder ? of(updateOrderAction(newOrder)) : EMPTY,
        shouldUpdateBulkOrder
          ? of(updateBulkOrderAction({ orders: newOrders }))
          : EMPTY,
      );
    }),
  );

const shouldNotFetchQuote = order =>
  _isEmpty(order.senderLocation) ||
  _isEmpty(validRecipientLocations(order)) ||
  _isEmpty(order.serviceTypes);

const fetchQuoteEpic = (action$, state$) =>
  action$.pipe(
    ofType(UPDATE_ORDER),
    switchMap(action => {
      const state = state$.value;
      const order = _get(state, 'booking.order', {});
      // Do nothing if just shallow update (no change on quote)
      if (shouldNotFetchQuote(order)) return EMPTY;

      const userInfo = state.app.userInfo;
      const payments = _get(state, 'booking.paymentMethods.result', []);
      const orderToQuote = toGetQuote(order, userInfo, payments);
      return merge(
        of(
          updateOrderShallowAction({
            ...order,
            fetchingQuote: true,
          }),
        ),
        of(fetchQuoteActionV2(orderToQuote)),
        action$.pipe(
          ofType(FETCH_QUOTE_V2.SUCCESS),
          switchMap(action2 => {
            const newOrder = {
              ...order,
              quote: action2.data,
              fetchingQuote: false,
            };
            const quoteToRender = getQuoteToRender(newOrder);
            newOrder.quote.forRender = quoteToRender;
            return concat(
              of(updateOrderShallowAction(newOrder)),
              getQuoteErrNotiObservable(quoteToRender),
            );
          }),
        ),
      );
    }),
  );

const fetchBulkQuoteEpic = (action$, state$) =>
  action$.pipe(
    ofType(UPDATE_BULK_ORDER),
    switchMap(action => {
      const state = state$.value;
      const { index, order, orders } = action.payload;
      // no need to fetch quote for a particular order
      if (orders || shouldNotFetchQuote(order)) return EMPTY;

      const userInfo = state.app.userInfo;
      const payments = _get(state, 'booking.paymentMethods.result', []);
      const orderToQuote = toGetQuote(order, userInfo, payments);
      return merge(
        of(
          updateBulkOrderShallowAction({
            index,
            order: {
              ...order,
              fetchingQuote: true,
            },
          }),
        ),
        of(fetchQuoteActionV2(orderToQuote)),
        action$.pipe(
          ofType(FETCH_QUOTE_V2.SUCCESS),
          switchMap(action2 => {
            const newOrder = {
              ...order,
              quote: action2.data,
              fetchingQuote: false,
            };
            const quoteToRender = getQuoteToRender(newOrder);
            newOrder.quote.forRender = quoteToRender;
            return concat(
              of(
                updateBulkOrderShallowAction({
                  index,
                  order: newOrder,
                }),
              ),
              getQuoteErrNotiObservable(quoteToRender),
            );
          }),
        ),
      );
    }),
  );

export const extractErrors = createdOrders => {
  if (createdOrders.length === 1 && createdOrders[0].errors) {
    return createdOrders[0].errors.map(errorObj => errorObj.message);
  }
  const errorMsgs = [];
  createdOrders.forEach((order, orderIndex) => {
    if (!order.errors || _isEmpty(order.errors)) return;
    order.errors.forEach(errorObj => {
      errorMsgs.push(`Order ${orderIndex + 1}: ${errorObj.message}`);
    });
  });
  return errorMsgs;
};

export const isGrabPayPaymentFailed = createdOrders => {
  const errorList = [];
  createdOrders.forEach(order => {
    if (!order.errors || _isEmpty(order.errors)) return;
    order.errors.forEach(errorObj => {
      if (errorObj.errorCode === 1022) {
        errorList.push(true);
      }
    });
  });
  return errorList.length > 0;
};

const createOrderSuccessEpic = (action$, state$) =>
  action$.pipe(
    ofType(CREATE_ORDER_V2.SUCCESS),
    switchMap(action => {
      const state = state$.value;
      const createdOrders = _get(action, 'data.deliveries', []);
      const totalOrderQty = createdOrders.length;
      const successOrderQty = createdOrders.reduce(
        (acc, order) => (_isEmpty(order.errors) ? acc + 1 : acc),
        0,
      );
      const failedOrderQty = totalOrderQty - successOrderQty;
      const errorMsgs = extractErrors(createdOrders);

      const countryCode = getUserCountryCodeFromState(state);
      const grabPayEror = isGrabPayPaymentFailed(createdOrders);

      const notifications = _compact([
        getSuccessNotification(successOrderQty),
        getFailedNotification(failedOrderQty, totalOrderQty, errorMsgs),
      ]);

      return grabPayEror
        ? concat(
            of(pushNotificationAction(notifications)),
            of(getAllPaxPaymentMethodsV2(countryCode)),
          )
        : of(pushNotificationAction(notifications));
    }),
  );

const createOrderFailEpic = (action$, state$) =>
  action$.pipe(
    ofType(CREATE_ORDER_V2.ERROR),
    switchMap(action => {
      const code = _get(action, 'data.code', '');
      const errorMessage = _get(action, 'data.message', '');
      if (action.status === 400 && code === 141) {
        // eslint-disable-next-line
        const notifications = getFailedConcurrentBookingNotification(
          errorMessage,
        );
        return of(pushNotificationAction(notifications));
      }

      const notifications = [getFailedNotification(1, 1, [errorMessage])];
      return of(pushNotificationAction(notifications));
    }),
  );

const createOrderFailV1Epic = (action$, state$) =>
  action$.pipe(
    ofType(CREATE_ORDER.ERROR),
    switchMap(action => {
      const errorMessage = _get(action, 'data.message', '');
      const notifications = [getFailedNotification(1, 1, [errorMessage])];

      return of(pushNotificationAction(notifications));
    }),
  );

const fetchPaymentMethodforGroup = (companyID, countryCode, groupID) => {
  const result = fetchUtil(
    endpoints.getPaymentDetailsEpt(companyID, countryCode, groupID),
    {
      method: 'GET',
      headers: buildHeaders(null, null, companyID, true),
      credentials: buildCredentials(),
    },
  ).then(resp => resp.result || null);
  return result;
};

const fetchAllPaymentMethodsEpic = (action$, state$) =>
  action$.pipe(
    ofType(FETCH_ALL_PAYMENT_METHODS),
    mergeMap(async action => {
      const company = _get(action, 'payload.company');
      const group = _get(action, 'payload.group');
      if (!company || !group) return EMPTY;

      const result = await fetchPaymentMethodforGroup(
        company.id,
        company.countryCode,
        group.id,
      );
      return getAllPaymentMethodsSuccessV2({ result });
    }),
    catchError(e => of(globalInitFailureAction())),
  );

const fetchAllPaxPaymentMethodsEpic = (action$, state$) =>
  action$.pipe(
    ofType(PAX_FETCH_ALL_PAYMENT_METHODS),
    switchMap(action => {
      const countryCode = _get(action, 'payload.countryCode');
      if (!countryCode) return EMPTY;
      return fetchPaxPaymentMethods(countryCode);
    }),
    switchMap(result => {
      // avoid case where result.response = { methods: null }
      const paymentMethods = _get(result, 'response.methods', []) || [];
      // renaming fields to match format of payment methods returned for corporate customers
      const transformedPaymentMethods = paymentMethods.map(pm => ({
        ...pm,
        paymentTypeID: pm.paymentTypeID.toString(),
        refNumber: _get(pm, 'payload.Last4', ''),
        expiryDate: _get(pm, 'payload.Expiry'),
        enabled: _get(pm, 'isPaymentAllowed', false),
      }));
      return of(
        getAllPaxPaymentMethodsSuccessV2({ result: transformedPaymentMethods }),
      );
    }),
    catchError(e => of(globalInitFailureAction())),
  );

const clearOrdersEpic = (action$, state$) =>
  action$.pipe(
    ofType(CLEAR_ORDERS_ACTION_V2),
    mergeMap(() => {
      const state = state$.value;
      const userSafeID = _get(state, 'app.userInfo.user.safeID');
      const cityID = _get(state, 'app.userInfo.selectedCity.cityID');
      if (!cityID) return EMPTY;
      const serviceTypes = _get(state, `booking.serviceTypeMap.${cityID}`);
      if (!serviceTypes) return EMPTY;
      const serviceTypeField = {
        name: FIELD_NAME.SERVICE_TYPE,
        value: _get(serviceTypes, '[0].ID', '').toString(),
      };
      const newOrder = {
        serviceTypes,
        [FIELD_NAME.SERVICE_TYPE]: serviceTypeField,
      };
      return of(
        updateOrderShallowAction({
          ...getSenderStorage(cityID, userSafeID),
          ...newOrder,
        }),
        updateBulkOrderShallowAction({ orders: [newOrder] }),
      );
    }),
  );

const failedToOptimizeRoute = errorMessage =>
  of(
    {
      type: LOCATION_DRAWER_ROUTE_OPTIMIZATION.ERROR,
    },
    pushNotificationAction({
      type: NOTIFICATION_TYPE.WARNING,
      message: I18n.t('v2.locationDrawer.failedToOptimizeRoute'),
      description:
        I18n.t('v2.locationDrawer.failedToOptimizeRouteDesc') +
        (errorMessage ? ` [${errorMessage}]` : ''),
    }),
  );

const routeOptimizationEpic = (action$, state$) =>
  action$.pipe(
    ofType(LOCATION_DRAWER_ROUTE_OPTIMIZATION.START),
    mergeMap(() => {
      const state = state$.value;
      const order = _get(state, 'booking.order', {});
      const userInfo = state.app.userInfo;
      const routeOptimisationRequest = toRouteOptimisationRequest(
        order,
        userInfo,
      );
      const optimizedRouteQuote$ = getOptimizedRoute(
        routeOptimisationRequest,
        getCompanyID(userInfo),
      ).pipe(catchError(error => of({ error })));
      // design is for this to always take at least 1 second
      const timer$ = timer(1000);
      return forkJoin(optimizedRouteQuote$, timer$);
    }),
    mergeMap(([result, _]) => {
      const err = _get(result, 'error');
      if (err) {
        return failedToOptimizeRoute(err);
      }

      const quoteV2 = _get(result, 'response.data.quote');
      if (!quoteV2) {
        return failedToOptimizeRoute();
      }

      const paxExpressError = _get(result, 'response.data.paxExpressError');
      if (paxExpressError) {
        return failedToOptimizeRoute(_get(paxExpressError, 'message'));
      }

      const state = state$.value;
      const alternativeFare = getAlternativeFare(quoteV2);
      const optimizedSteps = getOptimizedSteps(alternativeFare);
      if (
        alternativeFare &&
        optimizedSteps.length !==
          1 + _get(state, 'booking.order.recipientLocations', []).length
      ) {
        return failedToOptimizeRoute();
      }

      const previousQuoteForRender = _clone(
        _get(state, 'booking.order.quote.forRender'),
      );

      return of({
        type: LOCATION_DRAWER_ROUTE_OPTIMIZATION.SUCCESS,
        payload: {
          quoteV2,
          optimizeQuotesStatus: {
            addressFieldsNeedReorder: !!alternativeFare,
            status: !alternativeFare
              ? QUOTE_OPTIMIZED
              : QUOTE_OPTIMIZED_ALLOW_REVERT,
            previousQuoteForRender,
          },
        },
      });
    }),
  );

export default combineEpics(
  bookAgainEpic,
  updateOrderEpic,
  fetchServiceTypeEpic,
  fetchQuoteEpic,
  fetchBulkQuoteEpic,
  createOrderSuccessEpic,
  createOrderFailEpic,
  createOrderFailV1Epic,
  updateBulkOrderEpic,
  fetchAllPaymentMethodsEpic,
  fetchAllPaxPaymentMethodsEpic,
  clearOrdersEpic,
  routeOptimizationEpic,
);
